import React, { useEffect, useState } from "react";
import { BellRing, Loader2, Save } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "./ui/sonner";

const TARGETS = [["sales_vp", "销售 VP", "Sales VP"], ["ceo", "CEO", "CEO"]];

/* 评分催办升级设置（管理员 / CEO 可改；VP 只读） */
export function RatingEscalationSettings({ api, zh }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/settings/rating-escalation").then(({ data }) => setCfg(data)).catch(() => setCfg(null)); }, [api]);
  if (!cfg) return null;
  const toggleTarget = (t) => setCfg((c) => ({ ...c, targets: c.targets.includes(t) ? c.targets.filter((x) => x !== t) : [...c.targets, t] }));
  const save = async () => {
    if (!cfg.targets.length) { toast.error(zh ? "至少选择一个升级对象" : "Pick at least one target"); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/settings/rating-escalation", { enabled: cfg.enabled, days: Number(cfg.days), targets: cfg.targets, notifyManager: cfg.notifyManager });
      setCfg(data); toast.success(zh ? "催办升级设置已保存" : "Saved");
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "保存失败" : "Save failed")); }
    setSaving(false);
  };
  const ro = !cfg.canEdit;
  return (
    <div data-testid="rating-escalation-settings" className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <BellRing className="h-5 w-5 text-amber-600" />
        <h2 className="text-base font-bold text-slate-900">{zh ? "通话评分催办升级" : "Rating escalation"}</h2>
        <span className="text-xs text-slate-400">{zh ? "每日 09:05（洛杉矶时间）扫描各分公司销售经理待评录音" : "Daily 09:05 LA"}</span>
        <label className="ml-auto inline-flex items-center gap-2 cursor-pointer select-none">
          <span className="text-sm text-slate-600">{cfg.enabled ? (zh ? "已开启" : "On") : (zh ? "已关闭" : "Off")}</span>
          <button type="button" role="switch" aria-checked={cfg.enabled} disabled={ro} data-testid="rating-escalation-toggle" onClick={() => setCfg((c) => ({ ...c, enabled: !c.enabled }))}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${cfg.enabled ? "bg-emerald-500" : "bg-slate-300"} ${ro ? "opacity-60 cursor-not-allowed" : ""}`}>
            <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform ${cfg.enabled ? "translate-x-5" : "translate-x-0.5"}`} />
          </button>
        </label>
      </div>
      <div className="grid sm:grid-cols-3 gap-4 text-sm">
        <label className="space-y-1">
          <span className="text-xs font-semibold text-slate-500">{zh ? "待评超过多少天升级" : "Escalate after (days)"}</span>
          <div className="flex items-center gap-2">
            <input type="number" min={1} max={30} value={cfg.days} disabled={ro} data-testid="rating-escalation-days" onChange={(e) => setCfg((c) => ({ ...c, days: e.target.value }))} className="w-24 rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm" />
            <span className="text-slate-500">{zh ? "天（1–30）" : "days (1–30)"}</span>
          </div>
        </label>
        <div className="space-y-1">
          <span className="text-xs font-semibold text-slate-500">{zh ? "升级对象" : "Escalate to"}</span>
          <div className="flex items-center gap-4 pt-1">
            {TARGETS.map(([k, z, e]) => (
              <label key={k} className="inline-flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={cfg.targets.includes(k)} disabled={ro} data-testid={`rating-escalation-target-${k}`} onChange={() => toggleTarget(k)} />{zh ? z : e}
              </label>
            ))}
          </div>
        </div>
        <label className="inline-flex items-center gap-1.5 cursor-pointer pt-5">
          <input type="checkbox" checked={cfg.notifyManager} disabled={ro} data-testid="rating-escalation-notify-manager" onChange={(e) => setCfg((c) => ({ ...c, notifyManager: e.target.checked }))} />{zh ? "同时再催经理本人" : "Also remind the manager"}
        </label>
      </div>
      <div className="flex items-center justify-between">
        <p className="text-[11px] text-slate-400" data-testid="rating-escalation-updated">{cfg.updatedAt ? (zh ? `最近修改：${cfg.updatedBy || ""} ${new Date(cfg.updatedAt).toLocaleString("en-US")}` : `Updated by ${cfg.updatedBy || ""}`) : (zh ? "使用默认值：3 天 → 销售 VP" : "Defaults: 3 days → Sales VP")}</p>
        {ro ? <p className="text-[11px] text-slate-400">{zh ? "仅管理员 / CEO 可修改" : "Admin / CEO only"}</p>
          : <Button data-testid="rating-escalation-save" onClick={save} disabled={saving} className="bg-amber-600 hover:bg-amber-700 text-white">{saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}{zh ? "保存设置" : "Save"}</Button>}
      </div>
    </div>
  );
}
